import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMoney, STATUS_META } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { FileText, CheckCircle2, Eye, TrendingUp, Plus, ArrowUpRight, Flame, Timer } from "lucide-react";

const fmtDelay = (h) => {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(h < 10 ? 1 : 0)} h`;
  return `${(h / 24).toFixed(1)} j`;
};

const KPI = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="bg-card border border-border rounded-sm p-6 transition-all duration-200 hover:border-primary/50">
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">{label}</span>
      <Icon className="w-[18px] h-[18px] text-primary" strokeWidth={2} />
    </div>
    <p className="font-heading text-3xl font-bold tracking-tight">{value}</p>
    {sub && <p className="text-sm text-muted-foreground mt-1">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);

  const createNew = async () => {
    const { data: p } = await api.post("/proposals", { title: "Nouvelle proposition", sections: [] });
    navigate(`/proposals/${p.id}/edit`);
  };

  if (!data) return <div className="p-12 text-muted-foreground">Chargement…</div>;

  const statusOrder = ["brouillon", "envoyée", "vue", "acceptée", "refusée"];
  const chartData = statusOrder.map((s) => ({ name: STATUS_META[s].label, value: data.by_status[s] || 0, key: s }));
  const colors = { brouillon: "#9CA3AF", "envoyée": "#3B82F6", vue: "#F59E0B", "acceptée": "#10B981", "refusée": "#F43F5E" };

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Vue d'ensemble</p>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Bonjour, {user?.name?.split(" ")[0]} 👋</h1>
        </div>
        <button onClick={createNew} data-testid="dashboard-new-proposal"
          className="flex items-center gap-2 bg-primary text-white px-4 h-11 rounded-sm font-semibold hover:bg-primary/90 transition-all duration-200">
          <Plus className="w-4 h-4" /> Nouvelle proposition
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <KPI icon={FileText} label="Propositions" value={data.total_proposals} sub={`${data.accepted} acceptées`} testid="kpi-total" />
        <KPI icon={TrendingUp} label="Taux de signature" value={`${data.win_rate}%`} sub="sur les envoyées" testid="kpi-winrate" />
        <KPI icon={CheckCircle2} label="Valeur signée" value={fmtMoney(data.value_won)} sub="contrats gagnés" testid="kpi-won" />
        <KPI icon={Eye} label="Vues totales" value={data.total_views} sub={`Pipeline ${fmtMoney(data.pipeline)}`} testid="kpi-views" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-6">
          <h3 className="font-heading text-lg font-semibold mb-6">Propositions par statut</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} barSize={48}>
              <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#6B7280" }} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#6B7280" }} width={28} />
              <Tooltip cursor={{ fill: "#F3F4F6" }} contentStyle={{ borderRadius: 4, border: "1px solid #E5E7EB", fontSize: 13 }} />
              <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                {chartData.map((e) => <Cell key={e.key} fill={colors[e.key]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border rounded-sm p-6">
          <h3 className="font-heading text-lg font-semibold mb-4">Activité récente</h3>
          <div className="space-y-1">
            {data.recent.length === 0 && <p className="text-sm text-muted-foreground">Aucune proposition pour l'instant.</p>}
            {data.recent.map((p) => {
              const meta = STATUS_META[p.status] || STATUS_META.brouillon;
              return (
                <button key={p.id} onClick={() => navigate(`/proposals/${p.id}/edit`)} data-testid={`recent-${p.id}`}
                  className="w-full text-left flex items-center gap-3 py-3 border-b border-border last:border-0 group">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{p.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{p.client_name || "Sans client"} · {fmtMoney(p.total, p.currency)}</p>
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-sm border font-medium ${meta.color}`}>{meta.label}</span>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Advanced analytics: engagement + decision delay */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="bg-card border border-border rounded-sm p-6 flex flex-col" data-testid="dashboard-engagement">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Engagement moyen</span>
            <Flame className="w-[18px] h-[18px] text-primary" strokeWidth={2} />
          </div>
          <div className="flex items-end gap-2">
            <p className="font-heading text-5xl font-bold tracking-tight tabular-nums">{data.avg_engagement}</p>
            <span className="text-muted-foreground mb-2 text-sm">/ 100</span>
          </div>
          <div className="mt-4 h-2.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-700" style={{ width: `${data.avg_engagement}%` }} />
          </div>
          <p className="text-xs text-muted-foreground mt-3">Score moyen d'attention des prospects (vues, temps, couverture, scroll, décision).</p>
        </div>

        <div className="bg-card border border-border rounded-sm p-6 flex flex-col" data-testid="dashboard-decision-delay">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Délai moyen de décision</span>
            <Timer className="w-[18px] h-[18px] text-primary" strokeWidth={2} />
          </div>
          <p className="font-heading text-5xl font-bold tracking-tight">{fmtDelay(data.avg_decision_hours)}</p>
          <p className="text-sm text-muted-foreground mt-2">
            {data.decided_count > 0
              ? `Sur ${data.decided_count} proposition${data.decided_count > 1 ? "s" : ""} décidée${data.decided_count > 1 ? "s" : ""}`
              : "Aucune décision enregistrée pour l'instant"}
          </p>
          <p className="text-xs text-muted-foreground mt-auto pt-3">Temps écoulé entre l'envoi et l'acceptation ou le refus.</p>
        </div>

        <div className="bg-card border border-border rounded-sm p-6" data-testid="dashboard-top-proposals">
          <h3 className="font-heading text-lg font-semibold mb-4">Propositions les plus engageantes</h3>
          <div className="space-y-3">
            {(!data.top_proposals || data.top_proposals.filter((t) => t.engagement > 0).length === 0) && (
              <p className="text-sm text-muted-foreground">Pas encore d'engagement mesuré.</p>
            )}
            {(data.top_proposals || []).filter((t) => t.engagement > 0).map((t) => (
              <button key={t.id} onClick={() => navigate(`/proposals/${t.id}/analytics`)} data-testid={`top-proposal-${t.id}`}
                className="w-full text-left group">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <span className="text-sm font-medium truncate group-hover:text-primary transition-colors">{t.title}</span>
                  <span className="text-sm font-heading font-bold tabular-nums shrink-0">{t.engagement}</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${t.engagement}%` }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
